import {Box, List, ListSubheader, Typography} from '@mui/material';
import './TeamTranscripts.scss';
import {groupTranscriptByDate} from '../../../../../utils/groupTranscriptsByDate';
import {t} from 'i18next';
import {useTranscriptPanelSort} from '../../transcript-panel/TranscriptPanelContext';
import {
  TranscriptItemDataType,
  TranscriptSortKey,
} from '../../AiInvestigateUtils';
import TranscriptListItem from '../transcript-list-item/TranscriptListItem';
import ArrLoadingSpinner from '../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import React, {useEffect, useMemo, useRef, useState} from 'react';

interface TeamTranscriptsProps {
  transcripts: TranscriptItemDataType[];
  selectedTranscriptId?: number;
  onTranscriptSelect?: (selectedTranscript: {
    transcriptId: number;
    isOwner: boolean;
    indexId: number;
    indexName: string;
    isActiveIndex: boolean;
    transcriptName: string;
    ownerName: string;
    documentCount?: number;
  }) => void;
  isFetching: boolean;
  isLoading: boolean;
  onScroll: (e: React.UIEvent<HTMLDivElement>) => void;
}

const TeamTranscripts: React.FC<TeamTranscriptsProps> = ({
  transcripts,
  selectedTranscriptId,
  onTranscriptSelect,
  isFetching,
  isLoading,
  onScroll,
}) => {
  const [focusedId, setFocusedId] = useState(-1);
  const focusViaKeyboardRef = useRef(false);
  const selectedSortKey = useTranscriptPanelSort();

  const isNewestFirst =
    selectedSortKey === TranscriptSortKey.ModifiedNewest ||
    selectedSortKey === TranscriptSortKey.CreatedNewest;
  const sortType = selectedSortKey.includes('modified')
    ? 'modified'
    : 'created';
  const groupData = useMemo(
    () => groupTranscriptByDate(transcripts, sortType, isNewestFirst),
    [transcripts, sortType, isNewestFirst]
  );
  const sortedGroups = Object.entries(groupData).sort((a, b) =>
    isNewestFirst
      ? b[1].sortDate - a[1].sortDate
      : a[1].sortDate - b[1].sortDate
  );

  const flattenedItems = sortedGroups.flatMap(([, group]) => group.items);
  const orderedIds = flattenedItems.map((item) => item.transcriptId);
  const isFetchingRef = useRef(false);

  useEffect(() => {
    isFetchingRef.current = isFetching;
  }, [isFetching]);

  return (
    <Box
      className="team-transcript-list-container"
      onScroll={onScroll}
      data-testid="team-transcripts-container"
    >
      <List className="transcript-list" disablePadding>
        {isFetching && flattenedItems.length === 0 && (
          <Box className="spinner-center">
            <ArrLoadingSpinner size={32} />
          </Box>
        )}
        {!isFetching && flattenedItems.length === 0 && (
          <Box className="no-transcripts-container">
            <Typography
              className="transcript-panel-no-transcripts typo-text body3_size"
              data-testid="team-transcripts-no-transcripts"
            >
              {t('ai.investigate.transcriptPanel.noTranscripts')}
            </Typography>
          </Box>
        )}

        {flattenedItems.length > 0 &&
          sortedGroups.map(([dateLabel, groups], index) => (
            <React.Fragment key={dateLabel}>
              <ListSubheader
                disableGutters
                className="typo-text label3_size date-header"
              >
                <Typography
                  variant="caption"
                  data-testid={`team-transcripts-date-${index}`}
                >
                  {dateLabel}
                </Typography>
              </ListSubheader>
              {groups.items.map((item) => {
                const index = flattenedItems.findIndex(
                  (i) => i.transcriptId === item.transcriptId
                );
                return (
                  <TranscriptListItem
                    key={item.transcriptId}
                    item={item}
                    index={index}
                    focusedId={focusedId}
                    setFocusedId={setFocusedId}
                    orderedIds={orderedIds}
                    focusViaKeyboardRef={focusViaKeyboardRef}
                    showAvatar
                    selected={selectedTranscriptId === item.transcriptId}
                    onClick={(clickedItem: TranscriptItemDataType) =>
                      onTranscriptSelect?.({
                        transcriptId: clickedItem.transcriptId,
                        isOwner: clickedItem.isMyTranscript,
                        indexId: clickedItem.indexID,
                        indexName: clickedItem.indexName,
                        isActiveIndex: clickedItem.isActiveIndex,
                        transcriptName: clickedItem.transcriptName,
                        ownerName: clickedItem.ownerName,
                        documentCount: clickedItem.indexDocumentCount,
                      })
                    }
                  />
                );
              })}
            </React.Fragment>
          ))}

        {isFetching && flattenedItems.length > 0 && (
          <Box className="spinner-bottom">
            <ArrLoadingSpinner size={24} />
          </Box>
        )}
      </List>
    </Box>
  );
};
export default TeamTranscripts;
